import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * MENINA launch free-gift controller (v3).
 *
 * The card is server-rendered by snippets/cart-gift.liquid, which decides — on
 * every drawer re-render — whether to show the offer and whether the gift should
 * be auto-removed (eyewear gone, gift still in cart).
 *
 * Behaviour lives in SINGLETON document-level listeners registered once per page
 * load, NOT per <cart-gift> element, because the drawer morphs/replaces its
 * contents on every change.
 *
 * Add and auto-remove use SEPARATE guards: the Add button is gated only by its
 * own `disabled` state, so an in-flight (or wedged) auto-remove can never block a
 * customer from clicking Add. That was the cause of "works once, dead after the
 * cart is emptied and refilled".
 */

const ADD_SELECTOR = '[data-cart-gift-add]';

/** Guards duplicate auto-removes of the same line; never blocks Add. */
let removingKey = null;

/** Section ids to re-render so the drawer morphs in place. */
function sectionsParam() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids).join(',');
}

async function fetchCart() {
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * @param {HTMLElement} host - the <cart-gift> element
 * @param {Element} button
 */
async function addGift(host, button) {
  if (button.hasAttribute('disabled')) return;
  button.setAttribute('disabled', '');

  try {
    const body = JSON.stringify({
      id: Number(host.dataset.variantId),
      quantity: 1,
      sections: sectionsParam(),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    });
    const data = await response.json();

    if (data.status) {
      console.error('[cart-gift] add failed', data);
      return;
    }

    const cart = await fetchCart();
    document.dispatchEvent(
      new CartAddEvent(cart ?? {}, 'cart-gift', { source: 'cart-gift', sections: data.sections })
    );
  } catch (error) {
    console.error('[cart-gift] add error', error);
  } finally {
    button.removeAttribute('disabled');
  }
}

/** @param {HTMLElement} host - the <cart-gift> element */
async function removeGift(host) {
  const key = host.dataset.lineKey;
  if (!key || removingKey === key) return;
  removingKey = key;

  try {
    const body = JSON.stringify({
      id: key,
      quantity: 0,
      sections: sectionsParam(),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    });
    const data = await response.json();

    if (data.errors) {
      console.error('[cart-gift] remove failed', data);
      return;
    }

    document.dispatchEvent(
      new CartUpdateEvent(data, 'cart-gift', { source: 'cart-gift', sections: data.sections })
    );
  } catch (error) {
    console.error('[cart-gift] remove error', error);
  } finally {
    removingKey = null;
  }
}

/** Pull the gift if the server flagged it for removal after the latest render. */
function enforceRemoval() {
  const host = /** @type {HTMLElement | null} */ (
    document.querySelector('cart-gift[data-should-remove="true"]')
  );
  if (host) removeGift(host);
}

if (!window.__cartGiftWired) {
  window.__cartGiftWired = true;

  document.addEventListener('click', (event) => {
    const target = event.target;
    const button = target instanceof Element ? target.closest(ADD_SELECTOR) : null;
    if (!button) return;
    const host = /** @type {HTMLElement | null} */ (button.closest('cart-gift'));
    if (!host) return;
    event.preventDefault();
    addGift(host, button);
  });

  // After each cart change the drawer morphs and refreshes our data attributes;
  // wait a frame so the morph has landed, then enforce auto-removal.
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    requestAnimationFrame(enforceRemoval);
  });

  // Catch a stale state present on initial load (e.g. gift in cart, no eyewear).
  requestAnimationFrame(enforceRemoval);
}

if (!customElements.get('cart-gift')) {
  customElements.define('cart-gift', class extends HTMLElement {});
}
